import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  MessageCircle,
  Send,
  User,
  Bot,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  ArrowLeft,
  Phone
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Session List
const SessionList = ({ sessions, selectedId, onSelect }) => {
  const getStatusStyle = (status) => {
    switch (status) {
      case 'waiting_for_admin': return 'bg-red-100 text-red-700';
      case 'active': return 'bg-green-100 text-green-700';
      case 'admin_joined': return 'bg-blue-100 text-blue-700';
      case 'closed': return 'bg-slate-100 text-slate-500';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'waiting_for_admin': return 'Waiting';
      case 'active': return 'AI Active';
      case 'admin_joined': return 'Live';
      case 'closed': return 'Closed';
      default: return status;
    }
  };

  return (
    <div className="space-y-1">
      {sessions.length === 0 && (
        <div className="text-center py-8 text-muted-foreground text-sm">No chat sessions yet</div>
      )}
      {sessions.map((s) => (
        <button
          key={s.session_id}
          data-testid={`chat-session-${s.session_id}`}
          onClick={() => onSelect(s)}
          className={`w-full text-left p-3 rounded-lg transition ${
            selectedId === s.session_id ? 'bg-blue-50 border border-blue-200' : 'hover:bg-slate-50 border border-transparent'
          }`}
        >
          <div className="flex items-center justify-between mb-1">
            <span className="font-medium text-sm truncate">{s.user_name || 'Guest'}</span>
            <Badge className={`text-[10px] ${getStatusStyle(s.status)}`}>{getStatusLabel(s.status)}</Badge>
          </div>
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground truncate max-w-[180px]">{s.last_message || 'No messages'}</p>
            {s.unread_admin > 0 && (
              <span className="bg-red-500 text-white text-[10px] rounded-full w-5 h-5 flex items-center justify-center font-bold">{s.unread_admin}</span>
            )}
          </div>
          {s.last_message_at && (
            <p className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {new Date(s.last_message_at).toLocaleString()}
            </p>
          )}
        </button>
      ))}
    </div>
  );
};

// Chat Conversation View
const ChatConversation = ({ session, onBack, onRefresh }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMessages = async () => {
    try {
      const res = await fetch(`${API_URL}/api/chat/sessions/${session.session_id}/messages`, {
        credentials: 'include'
      });
      if (res.ok) {
        const msgs = await res.json();
        setMessages(msgs);
      }
    } catch (err) {
      console.error('Failed to fetch messages:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
    pollRef.current = setInterval(fetchMessages, 4000);
    return () => clearInterval(pollRef.current);
  }, [session.session_id]);

  useEffect(() => { scrollToBottom(); }, [messages]);

  const joinChat = async () => {
    try {
      await fetch(`${API_URL}/api/admin/chat/sessions/${session.session_id}/join`, {
        method: 'POST',
        credentials: 'include'
      });
      toast.success('Joined chat');
      fetchMessages();
      onRefresh();
    } catch {
      toast.error('Failed to join chat');
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || sending) return;
    const msg = input.trim();
    setInput('');
    setSending(true);

    try {
      const res = await fetch(`${API_URL}/api/admin/chat/sessions/${session.session_id}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message: msg })
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(prev => [...prev, data]);
      }
    } catch {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const closeChat = async () => {
    try {
      await fetch(`${API_URL}/api/admin/chat/sessions/${session.session_id}/close`, {
        method: 'POST',
        credentials: 'include'
      });
      toast.success('Chat closed');
      fetchMessages();
      onRefresh();
    } catch {
      toast.error('Failed to close chat');
    }
  };

  const getMessageStyle = (type) => {
    switch (type) {
      case 'user': return 'bg-slate-100 text-slate-800';
      case 'ai': return 'bg-purple-50 text-purple-900 border border-purple-100';
      case 'admin': return 'bg-[#1a3c5e] text-white ml-auto';
      case 'system': return 'bg-amber-50 text-amber-800 text-center text-xs italic mx-auto';
      default: return 'bg-slate-100';
    }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin" /></div>;

  const canSend = session.status === 'admin_joined';
  const canJoin = session.status !== 'closed' && session.status !== 'admin_joined';
  const canClose = session.status !== 'closed';

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-slate-100 bg-white shrink-0">
        <div className="flex items-center gap-2">
          <button onClick={onBack} className="lg:hidden p-1 hover:bg-slate-100 rounded">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <p className="font-medium text-sm">{session.user_name || 'Guest'}</p>
            <p className="text-[11px] text-muted-foreground">{session.user_email || 'No email'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canJoin && (
            <Button size="sm" onClick={joinChat} data-testid="admin-join-chat" className="text-xs h-8">
              Join Chat
            </Button>
          )}
          {canClose && (
            <Button size="sm" variant="outline" onClick={closeChat} className="text-xs h-8 text-red-600 border-red-200 hover:bg-red-50">
              Close
            </Button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50/50">
        {messages.map((msg) => (
          <div
            key={msg.message_id}
            className={`max-w-[80%] px-3 py-2 rounded-xl text-sm leading-relaxed ${getMessageStyle(msg.sender_type)}`}
          >
            {msg.sender_type !== 'system' && (
              <div className="flex items-center gap-1 mb-0.5 text-[10px] font-medium opacity-70">
                {msg.sender_type === 'ai' ? <Bot className="w-3 h-3" /> : <User className="w-3 h-3" />}
                {msg.sender_name}
              </div>
            )}
            <p className="whitespace-pre-wrap">{msg.message}</p>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      {canSend && (
        <div className="p-3 border-t border-slate-100 bg-white shrink-0">
          <div className="flex items-center gap-2">
            <Input
              data-testid="admin-chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
              placeholder="Type your reply..."
              className="flex-1 text-sm"
              disabled={sending}
            />
            <Button
              data-testid="admin-chat-send"
              size="sm"
              onClick={sendMessage}
              disabled={!input.trim() || sending}
              className="h-9 w-9 p-0"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
      {session.status === 'closed' && (
        <div className="p-3 text-center text-sm text-muted-foreground bg-slate-50 border-t border-slate-100">
          This chat has been closed
        </div>
      )}
      {!canSend && session.status !== 'closed' && (
        <div className="p-3 text-center text-sm text-muted-foreground bg-amber-50 border-t border-amber-100">
          Join the chat to send messages
        </div>
      )}
    </div>
  );
};

// Main Admin Chat Tab Export
export default function AdminChatTab() {
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const pollRef = useRef(null);

  const fetchSessions = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/chat/sessions`, {
        credentials: 'include'
      });
      if (res.ok) setSessions(await res.json());
    } catch (err) {
      console.error('Failed to fetch sessions:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
    pollRef.current = setInterval(fetchSessions, 5000);
    return () => clearInterval(pollRef.current);
  }, []);

  const waitingCount = sessions.filter(s => s.status === 'waiting_for_admin').length;

  if (isLoading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="admin-chat-tab" className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden" style={{height: '600px'}}>
      <div className="flex h-full">
        {/* Session List */}
        <div className={`w-full lg:w-80 border-r border-slate-100 flex flex-col shrink-0 ${selectedSession ? 'hidden lg:flex' : ''}`}>
          <div className="p-3 border-b border-slate-100 flex items-center justify-between bg-white">
            <div className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-primary" />
              <span className="font-semibold text-sm">Live Chats</span>
            </div>
            {waitingCount > 0 && (
              <Badge className="bg-red-100 text-red-700 text-xs">{waitingCount} waiting</Badge>
            )}
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            <SessionList
              sessions={sessions}
              selectedId={selectedSession?.session_id}
              onSelect={setSelectedSession}
            />
          </div>
        </div>

        {/* Chat Area */}
        <div className={`flex-1 flex flex-col ${!selectedSession ? 'hidden lg:flex' : ''}`}>
          {selectedSession ? (
            <ChatConversation
              session={selectedSession}
              onBack={() => setSelectedSession(null)}
              onRefresh={fetchSessions}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              <div className="text-center">
                <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="text-sm">Select a conversation to view</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
